import React, { createContext, useContext } from 'react';
/**
 * Shared scroll-to-reviews logic that can be used across all spot details panels
 *
 * Takes refs to the reviews element and scrollable container and handles:
 * 1. Finding the accordion button
 * 2. Expanding the accordion if collapsed
 * 3. Scrolling smoothly to the reviews section within the container
 * 4. Accounting for sticky headers that may cover the content
 *
 * @param reviewsRef - React ref pointing to the reviews accordion element
 * @param spotDetailsContainerRef - React ref pointing to the scrollable container
 * @param stickyHeaderRef - Optional ref pointing to sticky header element that may cover content
 * @param additionalPadding - Optional additional padding in pixels (default: 0)
 *
 * @example
 * // In any SpotDetails component
 * _scrollToReviews = () => {
 *     scrollToReviewsElement(this._reviewsRef, this._spotDetailsContainerRef, this._stickyHeaderRef, 32);
 * };
 */
export const scrollToReviewsElement = (reviewsRef, spotDetailsContainerRef, stickyHeaderRef, additionalPadding = 0) => {
    if (!reviewsRef.current) {
        return;
    }
    const reviewsElement = reviewsRef.current;
    // Find the accordion button within the reviews section
    const accordionButton = reviewsElement.querySelector('button[aria-expanded="false"]');
    const scrollToElement = (delay = 0) => {
        setTimeout(() => {
            const container = spotDetailsContainerRef.current;
            if (!container) {
                return;
            }
            // Account for sticky header that may cover the reviews section
            // The header may not be sticky yet, but will become sticky after scrolling
            let stickyHeaderHeight = 0;
            if (stickyHeaderRef?.current) {
                const boxElement = stickyHeaderRef.current;
                // Get the actual sticky component (first child of the Box wrapper)
                const stickyElement = boxElement.firstElementChild || boxElement;
                const styles = window.getComputedStyle(stickyElement);
                // Account for the element if it's not display:none
                // It will become sticky/fixed after scrolling, so we need to reserve space for it
                if (styles.display !== 'none') {
                    stickyHeaderHeight = stickyElement.getBoundingClientRect()
                        .height;
                }
            }
            // Calculate position within the scrollable container
            const containerTop = container.getBoundingClientRect().top;
            const elementTop = reviewsElement.getBoundingClientRect().top;
            const basePadding = 16; // Base padding for breathing room
            const offset = elementTop -
                containerTop +
                container.scrollTop -
                stickyHeaderHeight -
                basePadding -
                additionalPadding;
            // Scroll the container to the reviews element
            container.scrollTo({
                top: offset,
                behavior: 'smooth',
            });
        }, delay);
    };
    if (accordionButton) {
        // Expand the accordion if it's collapsed
        accordionButton.click();
        // Wait for accordion animation to complete before scrolling
        scrollToElement(150);
    }
    else {
        // Accordion is already open, just scroll
        scrollToElement();
    }
};
const defaultContext = {
    scrollToReviews: () => {
        // No-op default implementation when called outside provider
    },
};
export const ScrollToReviewsContext = createContext(defaultContext);
/**
 * Hook to access scroll-to-reviews functionality in functional components
 * @returns ScrollToReviewsContextValue
 */
export const useScrollToReviews = () => {
    return useContext(ScrollToReviewsContext);
};
/**
 * HOC to inject scroll-to-reviews functionality into class components
 * @example
 * class FacilitySummary extends Component {
 *     handleClick = () => {
 *         this.props.scrollToReviews();
 *     };
 * }
 *
 * export default withScrollToReviews(FacilitySummary);
 */
export const withScrollToReviews = (Component) => {
    var _a;
    return _a = class WithScrollToReviews extends React.Component {
            render() {
                return (<Component {...this.props} scrollToReviews={this.context.scrollToReviews}/>);
            }
        },
        _a.displayName = `WithScrollToReviews(${Component.displayName || Component.name || 'Component'})`,
        _a.contextType = ScrollToReviewsContext,
        _a;
};
